// TanStack Query hooks for the ShipmentTrip endpoints.
//
// Mutations invalidate trips + occupancy + items together, because loading
// an item off a slot frees the slot (occupancy) and changes the item's
// status/location (items). Without the cross-invalidation the 2D map and
// the "to put away" panel would show stale data after a load/unload.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchTrips,
  fetchTrip,
  createTrip,
  loadItemOnTrip,
  unloadItemFromTrip,
  departTrip,
  fetchPackingList,
  fetchPickRoute,
} from './warehouseApi.js';

const READ_STALE_MS = 30_000;

export function useTrips(filters = {}) {
  return useQuery({
    queryKey: ['warehouse', 'trips', filters],
    queryFn: () => fetchTrips(filters),
    staleTime: READ_STALE_MS,
  });
}

export function useTrip(code) {
  return useQuery({
    queryKey: ['warehouse', 'trip', code],
    queryFn: () => fetchTrip(code),
    enabled: !!code,
    staleTime: READ_STALE_MS,
  });
}

export function usePackingList(code) {
  return useQuery({
    queryKey: ['warehouse', 'trip', code, 'packing-list'],
    queryFn: () => fetchPackingList(code),
    enabled: !!code,
    staleTime: READ_STALE_MS,
  });
}

export function usePickRoute(code) {
  return useQuery({
    queryKey: ['warehouse', 'trip', code, 'pick-route'],
    queryFn: () => fetchPickRoute(code),
    enabled: !!code,
    staleTime: READ_STALE_MS,
  });
}

function invalidateWarehouseAndTrips(queryClient) {
  queryClient.invalidateQueries({ queryKey: ['warehouse', 'trips'] });
  queryClient.invalidateQueries({ queryKey: ['warehouse', 'trip'] });
  queryClient.invalidateQueries({ queryKey: ['warehouse', 'occupancy'] });
  queryClient.invalidateQueries({ queryKey: ['warehouse', 'items'] });
  queryClient.invalidateQueries({ queryKey: ['warehouse', 'movements'] });
}

export function useCreateTrip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => createTrip(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse', 'trips'] });
    },
  });
}

export function useLoadItem(code) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serial }) => loadItemOnTrip(code, serial),
    onSuccess: () => invalidateWarehouseAndTrips(queryClient),
  });
}

export function useUnloadItem(code) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serial }) => unloadItemFromTrip(code, serial),
    onSuccess: () => invalidateWarehouseAndTrips(queryClient),
  });
}

export function useDepartTrip(code) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => departTrip(code),
    onSuccess: () => invalidateWarehouseAndTrips(queryClient),
  });
}
